import { useEffect, useState } from "react";
import axios from "axios";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { LogOut, Eye, TrendingUp, CalendarDays, FileText, Lock, Loader2, Globe, MapPin, ShieldCheck, MailCheck, PhoneCall, Trash2, Plus, Percent, Trophy } from "lucide-react";
import { SunMark } from "@/components/Sun";
import { SERVICES, CITIES } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "iss_admin_token";

const serviceName = (slug) => SERVICES.find((s) => s.slug === slug)?.name || slug;
const cityName = (slug) => CITIES.find((c) => c.slug === slug)?.name || slug;

const STATUS_STYLES = {
  new: "bg-amber-500/15 text-amber-400 border-amber-500/40",
  quoted: "bg-sky-500/15 text-sky-400 border-sky-500/40",
  won: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40",
  lost: "bg-slate-500/15 text-slate-400 border-slate-500/40",
};

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-slate-800 bg-[#111827] p-6">
    <div className="flex items-center gap-2 text-amber-500 mb-3">
      <Icon className="h-4 w-4" />
      <span className="text-xs font-mono uppercase tracking-widest">{label}</span>
    </div>
    <p className="font-mono text-3xl font-bold text-slate-50">{value}</p>
  </div>
);

const BarList = ({ rows, labelKey, testid }) => {
  const max = rows[0]?.views || 1;
  return (
    <div className="space-y-3">
      {rows.length === 0 && <p className="text-sm text-slate-500">No data yet. It appears once visitors arrive.</p>}
      {rows.map((r) => (
        <div key={r[labelKey]} data-testid={testid}>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-slate-300 truncate">{r[labelKey]}</span>
            <span className="font-mono text-slate-400 shrink-0 ml-3">{r.views}</span>
          </div>
          <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
            <div className="h-full rounded-full bg-amber-500" style={{ width: `${Math.max(4, (r.views / max) * 100)}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
};

const EMPTY_LEAD = { name: "", contact: "", service: "", city: "", source: "phone", notes: "" };

const inputCls = "w-full rounded-lg border border-slate-700 bg-slate-900 px-3.5 py-2.5 text-sm text-slate-100 focus:border-amber-500 focus:outline-none";

export default function Admin() {
  const [token, setToken] = useState(() => (typeof window === "undefined" ? "" : localStorage.getItem(TOKEN_KEY) || ""));
  const [data, setData] = useState(null);
  const [leads, setLeads] = useState([]);
  const [leadForm, setLeadForm] = useState(EMPTY_LEAD);
  const [leadSaving, setLeadSaving] = useState(false);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [reportStatus, setReportStatus] = useState("");

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${token}` } });

  useEffect(() => {
    document.title = "Admin Analytics — Indigenous Security Services";
    const m = document.createElement("meta");
    m.setAttribute("name", "robots");
    m.setAttribute("content", "noindex, nofollow");
    document.head.appendChild(m);
    return () => m.remove();
  }, []);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/admin/dashboard`, authHeaders())
      .then((r) => setData(r.data))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      });
    axios
      .get(`${API}/admin/leads`, authHeaders())
      .then((r) => setLeads(r.data))
      .catch(() => {});
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const r = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, r.data.token);
      setToken(r.data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const sendReport = async () => {
    setReportStatus("sending");
    try {
      await axios.post(`${API}/admin/send-weekly-report`, {}, authHeaders());
      setReportStatus("sent");
    } catch {
      setReportStatus("error");
    }
  };

  const refreshStats = () => {
    axios.get(`${API}/admin/dashboard`, authHeaders()).then((r) => setData(r.data)).catch(() => {});
  };

  const addLead = async (e) => {
    e.preventDefault();
    setLeadSaving(true);
    try {
      const r = await axios.post(`${API}/admin/leads`, leadForm, authHeaders());
      setLeads((ls) => [r.data, ...ls]);
      setLeadForm(EMPTY_LEAD);
      refreshStats();
    } catch {
      setError("Could not save the lead. Try again.");
    } finally {
      setLeadSaving(false);
    }
  };

  const setLeadStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/leads/${id}`, { status }, authHeaders());
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      refreshStats();
    } catch {}
  };

  const removeLead = async (id) => {
    try {
      await axios.delete(`${API}/admin/leads/${id}`, authHeaders());
      setLeads((ls) => ls.filter((l) => l.id !== id));
      refreshStats();
    } catch {}
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setData(null);
    setLeads([]);
  };

  if (!token) {
    return (
      <div data-testid="admin-login-page" className="min-h-[70vh] flex items-center justify-center px-4 py-20">
        <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-md rounded-2xl border border-slate-800 bg-[#111827] p-8">
          <div className="flex justify-center mb-6"><SunMark size={52} /></div>
          <h1 className="text-2xl font-bold font-display text-slate-50 text-center mb-1">Admin Analytics</h1>
          <p className="text-sm text-slate-400 text-center mb-8 flex items-center justify-center gap-2">
            <Lock className="h-3.5 w-3.5" /> Private — authorized access only
          </p>
          {error && (
            <p data-testid="admin-login-error" className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2.5 text-sm text-red-400">{error}</p>
          )}
          <label className="block text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">Email</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" className={`${inputCls} mb-5`} autoComplete="username" />
          <label className="block text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">Password</label>
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" className={`${inputCls} mb-6`} autoComplete="current-password" />
          <button type="submit" disabled={loading} data-testid="admin-login-button" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 px-6 py-3.5 text-sm font-bold text-slate-950 transition-all hover:bg-amber-400 disabled:opacity-60">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Sign In
          </button>
        </form>
      </div>
    );
  }

  const cityRows = (data?.byCity || []).map((r) => ({ ...r, name: cityName(r.slug) }));
  const serviceRows = (data?.byService || []).map((r) => ({ ...r, name: serviceName(r.slug) }));

  return (
    <div data-testid="admin-dashboard" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-amber-500 mb-2">Private Dashboard</p>
          <h1 className="text-3xl font-bold font-display text-slate-50">Traffic & Leads</h1>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={sendReport} disabled={reportStatus === "sending"} data-testid="send-report-button" className="inline-flex items-center gap-2 rounded-full border border-amber-500/50 bg-amber-500/10 px-5 py-2.5 text-sm font-semibold text-amber-400 hover:bg-amber-500/20 transition-colors disabled:opacity-60">
            {reportStatus === "sending" ? <Loader2 className="h-4 w-4 animate-spin" /> : <MailCheck className="h-4 w-4" />}
            {reportStatus === "sent" ? "Report Sent" : reportStatus === "error" ? "Send Failed — Retry" : "Email Me This Report"}
          </button>
          <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-5 py-2.5 text-sm font-semibold text-slate-200 hover:border-amber-500/60 hover:text-amber-400 transition-colors">
            <LogOut className="h-4 w-4" /> Sign Out
          </button>
        </div>
      </div>

      {!data ? (
        <div className="flex items-center gap-3 text-slate-400 py-20 justify-center">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading analytics…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
            <StatCard icon={Eye} label="Total Pageviews" value={data.total.toLocaleString()} testid="stat-total-views" />
            <StatCard icon={CalendarDays} label="Today" value={data.today.toLocaleString()} testid="stat-today-views" />
            <StatCard icon={TrendingUp} label="Last 7 Days" value={data.last7days.toLocaleString()} testid="stat-week-views" />
            <StatCard icon={PhoneCall} label="Leads (7 Days)" value={data.leadsWeek.toLocaleString()} testid="stat-leads-week" />
            <StatCard icon={Trophy} label="Leads Won" value={data.leadsWon.toLocaleString()} testid="stat-leads-won" />
            <StatCard icon={Percent} label="Visit → Lead Rate" value={`${data.conversion}%`} testid="stat-conversion" />
          </div>

          <section className="rounded-2xl border border-slate-800 bg-[#111827] p-6 mb-10" data-testid="daily-chart-section">
            <h2 className="font-display font-bold text-slate-50 mb-6">Daily Pageviews — Last 30 Days</h2>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.daily} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                  <defs>
                    <linearGradient id="viewsGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.45} />
                      <stop offset="100%" stopColor="#F59E0B" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                  <YAxis tick={{ fill: "#64748B", fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #334155", borderRadius: 12, fontSize: 13 }} labelStyle={{ color: "#F59E0B" }} />
                  <Area type="monotone" dataKey="views" stroke="#F59E0B" strokeWidth={2} fill="url(#viewsGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-800 bg-[#111827] p-6 mb-10" data-testid="lead-log-section">
            <h2 className="font-display font-bold text-slate-50 mb-1">Lead Log — Phone & Email Quotes</h2>
            <p className="text-sm text-slate-500 mb-6">Dispatch staff: log every incoming call or email here so website traffic connects to real business.</p>
            <form onSubmit={addLead} data-testid="lead-add-form" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
              <input required placeholder="Contact name *" value={leadForm.name} onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })} data-testid="lead-name-input" className={inputCls} />
              <input placeholder="Phone or email" value={leadForm.contact} onChange={(e) => setLeadForm({ ...leadForm, contact: e.target.value })} data-testid="lead-contact-input" className={inputCls} />
              <select value={leadForm.service} onChange={(e) => setLeadForm({ ...leadForm, service: e.target.value })} data-testid="lead-service-select" className={inputCls}>
                <option value="">Service (optional)</option>
                {SERVICES.map((s) => <option key={s.slug} value={s.name}>{s.name}</option>)}
              </select>
              <select value={leadForm.city} onChange={(e) => setLeadForm({ ...leadForm, city: e.target.value })} data-testid="lead-city-select" className={inputCls}>
                <option value="">City (optional)</option>
                {CITIES.map((c) => <option key={c.slug} value={c.name}>{c.name}</option>)}
              </select>
              <select value={leadForm.source} onChange={(e) => setLeadForm({ ...leadForm, source: e.target.value })} data-testid="lead-source-select" className={inputCls}>
                <option value="phone">Phone call</option>
                <option value="email">Email</option>
                <option value="other">Other / referral</option>
              </select>
              <input placeholder="Notes (site, hours, budget…)" value={leadForm.notes} onChange={(e) => setLeadForm({ ...leadForm, notes: e.target.value })} data-testid="lead-notes-input" className={inputCls} />
              <button type="submit" disabled={leadSaving} data-testid="lead-add-button" className="sm:col-span-2 lg:col-span-3 inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 px-6 py-3 text-sm font-bold text-slate-950 transition-all hover:bg-amber-400 disabled:opacity-60">
                {leadSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Log Lead
              </button>
            </form>
            <div className="space-y-2" data-testid="leads-table">
              {leads.length === 0 && <p className="text-sm text-slate-500">No leads logged yet. The next phone call or email quote goes here.</p>}
              {leads.map((l) => (
                <div key={l.id} data-testid="lead-row" className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-800/60 bg-slate-900/50 px-4 py-3">
                  <div className="min-w-40">
                    <p className="text-sm font-semibold text-slate-100">{l.name}</p>
                    <p className="text-xs text-slate-500 truncate">{l.contact || "—"}</p>
                  </div>
                  <div className="text-xs text-slate-400 flex-1 min-w-32">
                    {l.service && <p>{l.service}</p>}
                    {l.city && <p className="text-slate-500">{l.city}</p>}
                  </div>
                  <span className="rounded-full border border-slate-700 px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-widest text-slate-400">{l.source}</span>
                  <select value={l.status} onChange={(e) => setLeadStatus(l.id, e.target.value)} data-testid={`lead-status-${l.id}`} className={`rounded-full border px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-transparent cursor-pointer ${STATUS_STYLES[l.status] || STATUS_STYLES.new}`}>
                    <option value="new" className="bg-slate-900 text-slate-200">New</option>
                    <option value="quoted" className="bg-slate-900 text-slate-200">Quoted</option>
                    <option value="won" className="bg-slate-900 text-slate-200">Won</option>
                    <option value="lost" className="bg-slate-900 text-slate-200">Lost</option>
                  </select>
                  <span className="text-[10px] font-mono text-slate-600">{(l.created_at || "").slice(0, 10)}</span>
                  <button onClick={() => removeLead(l.id)} data-testid={`lead-delete-${l.id}`} aria-label="Delete lead" className="text-slate-600 hover:text-red-400 transition-colors">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </section>

          <div className="grid lg:grid-cols-3 gap-6 mb-10">
            <section className="rounded-2xl border border-slate-800 bg-[#111827] p-6" data-testid="sources-section">
              <h2 className="font-display font-bold text-slate-50 mb-5 flex items-center gap-2"><Globe className="h-4 w-4 text-amber-400" /> Traffic Sources</h2>
              <BarList rows={data.byReferrer} labelKey="source" testid="source-row" />
            </section>
            <section className="rounded-2xl border border-slate-800 bg-[#111827] p-6" data-testid="top-cities-section">
              <h2 className="font-display font-bold text-slate-50 mb-5 flex items-center gap-2"><MapPin className="h-4 w-4 text-amber-400" /> Top Cities by Views</h2>
              <BarList rows={cityRows} labelKey="name" testid="city-row" />
            </section>
            <section className="rounded-2xl border border-slate-800 bg-[#111827] p-6" data-testid="top-services-section">
              <h2 className="font-display font-bold text-slate-50 mb-5 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-amber-400" /> Top Services by Views</h2>
              <BarList rows={serviceRows} labelKey="name" testid="service-row" />
            </section>
          </div>

          <div className="grid lg:grid-cols-5 gap-6">
            <section className="lg:col-span-3 rounded-2xl border border-slate-800 bg-[#111827] p-6" data-testid="top-pages-section">
              <h2 className="font-display font-bold text-slate-50 mb-5">Top Pages</h2>
              <div className="space-y-2">
                {data.topPaths.length === 0 && <p className="text-sm text-slate-500">No pageviews recorded yet. Traffic appears here once visitors arrive.</p>}
                {data.topPaths.map((t, i) => (
                  <div key={t.path} data-testid="top-page-row" className="flex items-center gap-3 rounded-lg border border-slate-800/60 bg-slate-900/50 px-4 py-2.5">
                    <span className="font-mono text-xs text-amber-500/70 w-6">{i + 1}</span>
                    <a href={t.path} target="_blank" rel="noopener noreferrer" className="flex-1 text-sm text-slate-300 hover:text-amber-400 truncate transition-colors">{t.path}</a>
                    <span className="font-mono text-sm font-bold text-slate-100">{t.views}</span>
                  </div>
                ))}
              </div>
            </section>
            <section className="lg:col-span-2 rounded-2xl border border-slate-800 bg-[#111827] p-6" data-testid="type-breakdown-section">
              <h2 className="font-display font-bold text-slate-50 mb-5">Views by Page Type</h2>
              <BarList rows={data.byType} labelKey="type" testid="type-row" />
            </section>
          </div>
        </>
      )}
    </div>
  );
}
